const noop = (e) => e;

const $effect = Symbol("effect");
const $exec = Symbol("exec");
const $next = Symbol("next");
function fromAsync(value) {
  const res = value;
  res[$exec] = "async";
  res[$next] = (onSucceed, onFail) => fromAsync(res.then((r) => r[$next](onSucceed, onFail)));
  return res;
}

function succeed(value) {
  const res = [void 0, value];
  res[$exec] = "sync";
  res[$effect] = "right";
  res[$next] = (onSucceed = noop) => onSucceed(res);
  res.left = void 0;
  res.right = value;
  return res;
}
function fail(error) {
  const res = [error, void 0];
  res[$exec] = "sync";
  res[$effect] = "left";
  res[$next] = (onSucceed, onFail = noop) => onFail(res);
  res.left = error;
  res.right = void 0;
  return res;
}
const some = succeed;
const right = succeed;
const none = () => fail(null);
const left = (value) => fail(value);

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, key + "" , value);
class UnknownError extends Error {
  constructor() {
    super(...arguments);
    /*                                                 */
    __publicField(this, "name", "UnknownError");
  }
}

const mapErrorPassThrough = (error) => error;
const mapErrorDefault = (error) => new UnknownError(`try handler failed: ${error instanceof Error ? error.message : `${error}`}`, {
  cause: error
});

function succeedAsync(value) {
  return fromAsync(value.then(succeed));
}
function succeedAsyncWith(value) {
  return succeedAsync(Promise.resolve(value));
}
function failAsync(value) {
  return fromAsync(value.then(fail, fail));
}
function failAsyncWith(value) {
  return failAsync(Promise.reject(value));
}
const coerceFunction = (predicate) => typeof predicate !== "function" ? predicate : predicate();
function fromCondition(predicate, rightVal, leftVal) {
  return coerceFunction(predicate) ? succeed(coerceFunction(rightVal)) : fail(coerceFunction(leftVal));
}
function fromNullable(predicate) {
  const value = coerceFunction(predicate);
  return fromCondition(value !== null && value !== void 0, value, value);
}
function fromBoolean(predicate) {
  const value = coerceFunction(predicate);
  return fromCondition(value === true, value, value);
}
function fromCoerced(rightValue, leftValue) {
  const value = coerceFunction(rightValue);
  return fromCondition(!!value, value, coerceFunction(leftValue));
}
function invert(effect) {
  return effect[$next](
    (v) => fail(v[1]),
    (v) => succeed(v[0])
  );
}

function fromExpression(effect) {
  return effect;
}
function syncImmediate(handler) {
  return succeed(handler());
}
function syncEffectImmediate(handler) {
  return handler();
}
function promiseImmediate(handler) {
  return succeedAsync(handler());
}
function promiseEffectImmediate(handler) {
  return fromAsync(handler());
}
function trySyncImmediate(handler, mapErr) {
  try {
    return succeed(handler());
  } catch (error) {
    return mapErr ? fail(mapErr(error)) : fail(mapErrorDefault(error));
  }
}
function tryPromiseImmediate(handler, mapErr) {
  return fromAsync(
    Promise.resolve().then(handler).then(
      succeed,
      (error) => mapErr ? fail(mapErr(error)) : fail(mapErrorDefault(error))
    )
  );
}

function sync(handler) {
  return function syncHandler(...a) {
    return succeed(handler.apply(this, a));
  };
}
function syncEffect(handler) {
  return handler;
}
function promise(handler) {
  return function promiseHandler(...a) {
    return succeedAsync(handler.apply(this, a));
  };
}
function promiseEffect(handler) {
  return function promiseEffectHandler(...a) {
    return fromAsync(handler.apply(this, a));
  };
}
function trySync(handler, mapErr) {
  return function trySyncHandler(...a) {
    return trySyncImmediate(() => handler.apply(this, a), mapErr);
  };
}
function tryPromise(handler, mapErr) {
  return function tryPromiseHandler(...a) {
    return tryPromiseImmediate(() => handler.apply(this, a), mapErr);
  };
}

function map(mapper) {
  const onSuccess = (e) => succeed(mapper(e[1]));
  return (effect) => effect[$next](onSuccess);
}
function mapAsync(mapper) {
  const onSuccess = (e) => succeedAsync(mapper(e[1]));
  return (effect) => effect[$next](onSuccess);
}
function mapSpread(mapper) {
  const onSuccess = (e) => succeed(mapper(...e[1]));
  return (effect) => effect[$next](onSuccess);
}
function mapAsyncSpread(mapper) {
  const onSuccess = (e) => succeedAsync(mapper(...e[1]));
  return (effect) => effect[$next](onSuccess);
}
function flatMap(mapper) {
  const onSuccess = (e) => mapper(e[1]);
  return (effect) => effect[$next](onSuccess);
}
function flatMapSpread(mapper) {
  const onSuccess = (e) => mapper(...e[1]);
  return (effect) => effect[$next](onSuccess);
}
function mapError(mapper) {
  return (effect) => effect[$next](void 0, (e) => fail(mapper(e.left)));
}
function mapErrorAsync(mapper) {
  return (effect) => effect[$next](void 0, (e) => failAsync(mapper(e.left)));
}

function pipe(...args) {
  switch (args.length) {
    case 2:
      return args[1](args[0]);
    case 3:
      return args[2](args[1](args[0]));
    case 4:
      return args[3](args[2](args[1](args[0])));
    case 5:
      return args[4](args[3](args[2](args[1](args[0]))));
    case 6:
      return args[5](args[4](args[3](args[2](args[1](args[0])))));
    case 7:
      return args[6](args[5](args[4](args[3](args[2](args[1](args[0]))))));
    case 8:
      return args[7](args[6](args[5](args[4](args[3](args[2](args[1](args[0])))))));
    case 9:
      return args[8](args[7](args[6](args[5](args[4](args[3](args[2](args[1](args[0]))))))));
    case 10:
      return args[9](args[8](args[7](args[6](args[5](args[4](args[3](args[2](args[1](args[0])))))))));
    case 1:
      return args[0];
    case 0:
      return void 0;
    default:
      return args.slice(1).reduce((acc, fn) => fn(acc), args[0]);
  }
}
function pipeMap(...args) {
  const onSuccess = (e) => pipe(e, ...args);
  return (effect) => effect[$next](onSuccess);
}

function returnsEffect(handler) {
  return handler;
}

export { $effect as $, fromCoerced as A, invert as B, fromExpression as C, syncImmediate as D, syncEffectImmediate as E, promiseImmediate as F, promiseEffectImmediate as G, trySyncImmediate as H, tryPromiseImmediate as I, mapAsync as J, mapSpread as K, mapAsyncSpread as L, flatMap as M, flatMapSpread as N, mapError as O, mapErrorAsync as P, pipeMap as Q, returnsEffect as R, UnknownError as U, fromAsync as a, $next as b, some as c, succeedAsync as d, $exec as e, fail as f, tryPromise as g, mapErrorPassThrough as h, mapErrorDefault as i, sync as j, syncEffect as k, promise as l, map as m, none as n, promiseEffect as o, pipe as p, left as q, right as r, succeed as s, trySync as t, succeedAsyncWith as u, failAsync as v, failAsyncWith as w, fromCondition as x, fromNullable as y, fromBoolean as z };
/*                                       */
